import { Icon } from "@mdi/react";
import useResizeObserver from "@react-hook/resize-observer";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";

import { useApolloClient, useMutation as useApolloMutation } from "@apollo/client";
import { mdiFolderPlus, mdiMagnify } from "@mdi/js";
import { AntTreeNodeProps } from "antd/es/tree";
import { AxiosError } from "axios";
import { USER_ARPS } from "constants/settings.constants";
import debounce from "lodash/debounce";

import { FOLDERS_LIGHT, UPDATE_FOLDER_NAME } from "api/userArps";
import { updatePerformanceData } from "api/userForecasts";

import ArpsUploadWizard from "../../project/forecasts/arps-upload-wizard/ArpsUploadWizard";
import { useArpsUploadWizard } from "../../project/projects/hooks";
import { ORG_FORECAST_ID } from "../constants/constants";
import { useOrganizationForecasts } from "../hooks";
import { useDeleteOrganizationForecast } from "../hooks/useDeleteOrganizationForecasts";
import ForecastUploadInfo from "./ForecastUploadInfo";
import {
  Container,
  CreateFolderButton,
  EmptyState,
  EmptyStateText,
  HeaderContent,
  HeaderText,
  LoadingContainer,
  LoadingText,
  SearchContainer,
  StyledInput,
  StyledTree,
  Title,
  TreeContainer,
  TreeHeader
} from "./OrganizationForecasts.styles";
import OrganizationTreeNode from "./OrganizationTreeNode";

export default function OrganizationForecasts() {
  const {
    data: forecastData,
    loading: isLoading,
    error,
    refetch
  } = useOrganizationForecasts();
  const { delete: deleteForecast } = useDeleteOrganizationForecast();
  const arpsUploadWizard = useArpsUploadWizard();
  const apolloClient = useApolloClient();
  const [filteredTreeData, setFilteredTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [canEdit] = useState(true);
  const [height, setHeight] = useState(100);
  const [editingNodes, setEditingNodes] = useState<Record<string, string>>({});
  const [highlightedFolderIdForDragDrop, setHighlightedFolderForDragDrop] = useState<
    string | null
  >(null);
  const [wizardUploadLoading, setWizardUploadLoading] = useState(false);
  const [deletingNodeKey, setDeletingNodeKey] = useState<string | null>(null);
  const ref = useRef(null);

  const updatePerformanceDataMutation = useMutation(
    (folderName: string) => {
      return updatePerformanceData(folderName);
    },
    {
      onError: (err: AxiosError) => {
        toast.error(err?.response?.data);
      }
    }
  );

  const [updateFolderName] = useApolloMutation(UPDATE_FOLDER_NAME, {
    refetchQueries: [FOLDERS_LIGHT],
    onError: () => {
      toast.error(`Failed to update folder name`);
    }
  });
  const getNewFolderName = (forecastFolders, existingTreeData = []) => {
    let name = "New Folder";
    const existingNames = {};
    for (const item of forecastFolders) {
      existingNames[item.name] = true;
    }

    for (const item of existingTreeData) {
      if (item.title) {
        existingNames[item.title] = true;
      }
    }

    let number = 1;
    while (existingNames[name]) {
      name = `New Folder ${number++}`;
    }
    return name;
  };

  useLayoutEffect(() => {
    if (!ref || isLoading) {
      return;
    }
    setHeight(ref.current?.getBoundingClientRect()?.height);
  }, [ref, isLoading]);

  useResizeObserver(ref, (client) => {
    if (client.contentRect.height > 0) {
      setHeight(client.contentRect.height);
    }
  });

  const transformFoldersToTreeData = useCallback((folders): [] => {
    if (!folders || folders.length === 0) {
      return [];
    }

    const groupBy = function (xs, key) {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
      }, {});
    };

    return folders.flatMap((folder) => {
      if (!folder.forecasts || folder.forecasts.length === 0) {
        return [
          {
            title: folder.name,
            key: folder.folderId,
            children: [],
            reserveCategory: null
          }
        ];
      }

      const grouped = groupBy(folder.forecasts, "reserveCategory");
      return Object.entries(grouped).map(([reserveCategory, forecasts]) => ({
        title: folder.name,
        key: `${folder.folderId}${reserveCategory ? "_" + reserveCategory : ""}`,
        folderId: folder.folderId,
        reserveCategory: reserveCategory === "null" ? null : reserveCategory,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        children: (forecasts as any[]).map((forecast) => ({
          title: forecast.name,
          key: forecast.id,
          isLeaf: true,
          reserveCategory: forecast.reserveCategory
        }))
      }));
    });
  }, []);
  const [baseTreeData, setBaseTreeData] = useState([]);

  useEffect(() => {
    if (forecastData && forecastData.folders) {
      setBaseTreeData((prevTreeData) => {
        const treeData = transformFoldersToTreeData(forecastData.folders);

        const tempFolders = prevTreeData.filter(
          (folder) => folder.tempKey && folder.tempKey in editingNodes
        );

        return [...treeData, ...tempFolders];
      });
    }
  }, [forecastData?.folders, transformFoldersToTreeData, editingNodes]);

  useEffect(() => {
    setFilteredTreeData(baseTreeData);
  }, [baseTreeData]);

  /**
   * Process tree data to add className for org folders.
   * This allows CSS targeting of empty org folders with ant-tree-switcher-noop.
   */
  const processedTreeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processNode = (node: any): any => {
      const isFolder = !node.isLeaf;
      return {
        ...node,
        className: isFolder ? "org-folder-node" : node.className,
        children: node.children?.map(processNode)
      };
    };
    return filteredTreeData.map(processNode);
  }, [filteredTreeData]);

  /**
   * Checks folder properties for expansion behavior.
   * In OrganizationForecasts, all folders are org folders and cannot be expanded.
   */
  const getFolderInfo = useCallback(
    (node: { children?: unknown[]; isLeaf?: boolean }) => {
      // Leaf nodes (individual forecasts) are not folders
      if (node?.isLeaf) return { isFolder: false, forecastCount: 0 };

      const forecastCount = node?.children?.length ?? 0;

      return {
        isFolder: true,
        forecastCount
      };
    },
    []
  );

  /**
   * Custom switcher icon renderer for org folders.
   * All org folders hide the chevron (no tooltip needed - it's clear they can't expand).
   */
  const renderSwitcherIcon = useCallback(
    (props: AntTreeNodeProps) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodeData = props.data as any;
      const { isFolder } = getFolderInfo(nodeData);

      // For leaf nodes, return null to use default behavior
      if (props.isLeaf || nodeData?.isLeaf || !isFolder) {
        return null;
      }

      // All org folders hide the chevron (no tooltip needed)
      return <span className="hidden-switcher" />;
    },
    [getFolderInfo]
  );

  const onExpand = useCallback(
    (
      expandedKeysValue: string[],
      info?: { node: AntTreeNodeProps; expanded: boolean }
    ) => {
      // All org folders cannot be expanded, so just prevent expansion
      if (info?.expanded) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const expandingNode = info.node as any;
        const { isFolder } = getFolderInfo(expandingNode);

        if (isFolder) {
          // Don't update expanded keys - org folders cannot be expanded
          return;
        }
      }

      setExpandedKeys(expandedKeysValue);
    },
    [getFolderInfo]
  );

  const handleEditFolder = useCallback((node) => {
    setEditingNodes((prev) => ({
      ...prev,
      [node.key]: node.title
    }));
  }, []);
  const handleSaveEdit = useCallback(
    async (nodeKey: string, newTitle: string) => {
      if (!newTitle.trim()) return;

      try {
        if (nodeKey.startsWith("temp-")) {
          await updateFolderName({
            variables: {
              input: {
                projectId: ORG_FORECAST_ID,
                folderId: null, // null for new folder
                parentId: "",
                name: newTitle.trim(),
                type: USER_ARPS
              }
            }
          });
          if (refetch) {
            await refetch();
          }
        } else {
          // This is an existing folder being renamed
          const actualFolderId = nodeKey.includes("_") ? nodeKey.split("_")[0] : nodeKey;

          await updateFolderName({
            variables: {
              input: {
                projectId: ORG_FORECAST_ID,
                folderId: actualFolderId,
                parentId: "",
                name: newTitle.trim(),
                type: USER_ARPS
              }
            }
          });

          const updateTreeData = (data) =>
            data.map((folder) => {
              if (folder.folderId === actualFolderId || folder.key === actualFolderId) {
                return { ...folder, title: newTitle.trim() };
              }
              return folder;
            });
          setBaseTreeData(updateTreeData);
          setFilteredTreeData(updateTreeData);
        }
      } catch (error) {
        toast.error("Failed to save folder");
      }

      setEditingNodes((prev) => {
        const newState = { ...prev };
        delete newState[nodeKey];
        return newState;
      });
    },
    [updateFolderName, refetch]
  );

  const handleCancelEdit = useCallback((nodeKey: string) => {
    if (nodeKey.startsWith("temp-")) {
      setBaseTreeData((prev) => prev.filter((folder) => folder.tempKey !== nodeKey));
      setFilteredTreeData((prev) => prev.filter((folder) => folder.tempKey !== nodeKey));
    }

    setEditingNodes((prev) => {
      const newState = { ...prev };
      delete newState[nodeKey];
      return newState;
    });
  }, []);
  const handleDeleteFolder = useCallback(
    async (node) => {
      if (!node?.key) {
        return;
      }

      try {
        setDeletingNodeKey(node.key);

        const idsToDelete = [];

        const actualFolderId = node.folderId || node.key;

        if (node.folderId && node.reserveCategory) {
          if (node.children && node.children.length > 0) {
            node.children.forEach((child) => {
              idsToDelete.push({
                folderId: actualFolderId,
                id: child.key,
                type: "forecast"
              });
            });
          }
        } else {
          idsToDelete.push({
            folderId: actualFolderId,
            id: actualFolderId,
            type: "folder"
          });

          if (node.children && node.children.length > 0) {
            node.children.forEach((child) => {
              idsToDelete.push({
                folderId: actualFolderId,
                id: child.key,
                type: "forecast"
              });
            });
          }
        }

        await deleteForecast(idsToDelete);
        if (refetch) {
          await refetch();
        }

        toast.success(`Folder "${node.title}" deleted successfully`);
      } catch (error) {
        toast.error(`Failed to delete folder: ${error.message || "Unknown error"}`);
      } finally {
        setDeletingNodeKey(null);
      }
    },
    [deleteForecast, refetch]
  );
  const handleDeleteForecast = useCallback(
    async (node) => {
      if (!node?.key) {
        return;
      }

      try {
        setDeletingNodeKey(node.key);

        let parentFolderId = null;

        for (const folder of filteredTreeData) {
          if (
            folder.children &&
            folder.children.some((child) => child.key === node.key)
          ) {
            parentFolderId = folder.folderId || folder.key;
            break;
          }
        }

        const idsToDelete = [
          {
            folderId: parentFolderId || node.key,
            id: node.key,
            type: "forecast"
          }
        ];

        await deleteForecast(idsToDelete);

        if (refetch) {
          await refetch();
        }

        toast.success(`Forecast "${node.title}" deleted successfully`);
      } catch (error) {
        toast.error(`Failed to delete forecast: ${error.message || "Unknown error"}`);
      } finally {
        setDeletingNodeKey(null);
      }
    },
    [deleteForecast, refetch, filteredTreeData]
  );
  const handleWizardUploadSuccess = useCallback(
    async (response, requestData) => {
      if (apolloClient) {
        try {
          await apolloClient.refetchQueries({
            include: [FOLDERS_LIGHT]
          });
        } catch (error) {
          if (refetch) {
            await refetch();
          }
        }
      } else if (refetch) {
        await refetch();
      }

      setHighlightedFolderForDragDrop(null);
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success("Files uploaded successfully");
      }
      updatePerformanceDataMutation.mutate(requestData.folderId);
    },
    [apolloClient, refetch, updatePerformanceDataMutation]
  );

  const handleWizardUploadError = useCallback((error: unknown) => {
    const err = error as { response?: { data?: { error?: string }; status?: number } };
    if (err && err.response?.data) {
      if (err.response.status === 504) {
        toast.error("The server didn't respond in time. Please try again later.");
      } else {
        toast.error(err.response?.data.error || "Upload failed");
      }
    } else {
      toast.error("Error uploading forecast files");
    }
    setHighlightedFolderForDragDrop(null);
  }, []);

  const handleWizardLoadingChange = useCallback((isLoading: boolean) => {
    setWizardUploadLoading(isLoading);
  }, []);

  const uploadToOrganizationFolder = useCallback(
    async (folderName: string, folderId: string, files: File[]) => {
      if (!canEdit) {
        toast.error("Cannot upload files without edit permissions");
        return;
      }

      if (files.length === 0) {
        toast.error("No files selected");
        return;
      }

      arpsUploadWizard.openWizard(folderId, folderName, files);
    },
    [canEdit, arpsUploadWizard.openWizard]
  );

  const setHighlightedFolderDebounced = debounce(setHighlightedFolderForDragDrop, 100);
  const handleCreateNewFolder = useCallback(() => {
    const newFolderTitle = getNewFolderName(forecastData?.folders || [], baseTreeData);
    const tempKey = `temp-${Date.now()}`;

    const newFolder = {
      title: newFolderTitle,
      key: null,
      tempKey: tempKey
    };

    const updateTreeDataWithNewFolder = (data: []) => [...data, newFolder];

    setBaseTreeData(updateTreeDataWithNewFolder);
    setFilteredTreeData(updateTreeDataWithNewFolder);

    setEditingNodes((prev) => ({
      ...prev,
      [tempKey]: newFolderTitle
    }));
  }, [forecastData?.folders, baseTreeData]);
  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (!value.trim()) {
        setFilteredTreeData(baseTreeData);
        return;
      }

      const filtered = baseTreeData
        .map((folder) => {
          const matchingChildren =
            folder.children?.filter((child) =>
              child.title.toLowerCase().includes(value.toLowerCase())
            ) || [];

          const folderMatches = folder.title.toLowerCase().includes(value.toLowerCase());

          if (folderMatches || matchingChildren.length > 0) {
            return {
              ...folder,
              children: matchingChildren.length > 0 ? matchingChildren : folder.children
            };
          }

          return null;
        })
        .filter(Boolean);

      setFilteredTreeData(filtered);

      if (value.trim()) {
        const keysToExpand = filtered.map((folder) => folder.key);
        setExpandedKeys(keysToExpand);
      }
    },
    [baseTreeData]
  );

  const renderTreeNode = useCallback(
    (node) => {
      const nodeKey = node.tempKey || node.key;
      const isEditing = nodeKey in editingNodes;
      const editingValue = editingNodes[nodeKey] || "";

      return (
        <OrganizationTreeNode
          node={node}
          canEdit={canEdit}
          editingNodeKey={isEditing ? nodeKey : null}
          editingNodeValue={editingValue}
          highlightedFolderIdForDragDrop={highlightedFolderIdForDragDrop}
          wizardUploadLoading={wizardUploadLoading}
          deletingNodeKey={deletingNodeKey}
          setEditingNodeValue={(value: string) => {
            setEditingNodes((prev) => ({
              ...prev,
              [nodeKey]: value
            }));
          }}
          setHighlightedFolderForDragDrop={setHighlightedFolderForDragDrop}
          setHighlightedFolderDebounced={setHighlightedFolderDebounced}
          handleSaveEdit={handleSaveEdit}
          handleCancelEdit={() => handleCancelEdit(nodeKey)}
          handleEditFolder={handleEditFolder}
          handleDeleteFolder={handleDeleteFolder}
          handleDeleteForecast={handleDeleteForecast}
          uploadToOrganizationFolder={uploadToOrganizationFolder}
        />
      );
    },
    [
      canEdit,
      editingNodes,
      highlightedFolderIdForDragDrop,
      wizardUploadLoading,
      deletingNodeKey,
      handleEditFolder,
      handleDeleteFolder,
      handleDeleteForecast,
      handleSaveEdit,
      handleCancelEdit,
      uploadToOrganizationFolder,
      setHighlightedFolderDebounced
    ]
  );

  if (error) {
    return (
      <LoadingContainer>
        <LoadingText>Error loading organization forecasts: {error?.message}</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <>
      <div>
        <HeaderContent>
          <HeaderText>
            <Title>Organization Forecasts</Title>
          </HeaderText>
        </HeaderContent>
      </div>
      <Container>
        <ForecastUploadInfo />
        <TreeHeader>
          <SearchContainer>
            <StyledInput
              placeholder="Search forecasts..."
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<Icon path={mdiMagnify} size={1} />}
              allowClear
            />
          </SearchContainer>
          {canEdit && (
            <CreateFolderButton onClick={handleCreateNewFolder}>
              <Icon path={mdiFolderPlus} size={1} />
              <span>New Folder</span>
            </CreateFolderButton>
          )}
        </TreeHeader>
        <TreeContainer ref={ref}>
          <StyledTree
            showIcon={false}
            blockNode
            treeData={processedTreeData}
            expandedKeys={expandedKeys}
            height={height}
            virtual={true}
            onExpand={onExpand}
            autoExpandParent={false}
            titleRender={renderTreeNode}
            switcherIcon={renderSwitcherIcon}
          />
          {filteredTreeData.length === 0 && !isLoading && (
            <EmptyState>
              <EmptyStateText>
                {searchValue
                  ? "No forecasts match your search"
                  : "No forecasts available"}
              </EmptyStateText>
            </EmptyState>
          )}
        </TreeContainer>
      </Container>
      <ArpsUploadWizard
        type={USER_ARPS}
        arpsUploadWizard={arpsUploadWizard}
        onUploadSuccess={handleWizardUploadSuccess}
        onUploadError={handleWizardUploadError}
        onLoadingStateChange={handleWizardLoadingChange}
        projectId={ORG_FORECAST_ID}
      />
    </>
  );
}
